import * as preact from "preact";
import PropTypes from "prop-types";
import { stopPropagation } from "../../foundation/utils/legacy";
import Portal from "../Portal";

import noderef from "../../foundation/utils/noderef";

import "./style.css";

const { h } = preact;

const TOOLTIP_SHOW_DELAY = 500;
const TOOLTIP_HIDE_DELAY = 200;
const TOOLTIP_MARGIN = 12;
const TOOLTIP_OFFSET = 2;

/**
 * A tooltip component that displays the specified content when its target is hovered over.
 */
class Tooltip extends preact.Component {
    constructor(props) {
        super(props);

        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleTargetClick = this.handleTargetClick.bind(this);
        this.handleTooltipDidRender = this.handleTooltipDidRender.bind(this);

        this.state = {
            visible: false
        };
    }

    get delay() {
        const { delay } = this.props;

        return typeof delay === "undefined" ? TOOLTIP_SHOW_DELAY : delay;
    }

    handleMouseEnter() {
        clearTimeout(this.hideTimeout);
        clearTimeout(this.showTimeout);

        if (!this.state.visible) {
            this.showTimeout = setTimeout(() => {
                this.setState({ visible: true });
            }, this.delay);
        }
    }

    handleMouseLeave() {
        clearTimeout(this.hideTimeout);
        clearTimeout(this.showTimeout);

        this.hideTimeout = setTimeout(() => {
            this.setState({ visible: false });
        }, TOOLTIP_HIDE_DELAY);
    }

    handleTargetClick() {
        this.setState({ visible: false });
    }

    handleTooltipDidRender(tooltip) {
        const targetRect = this.nodeRefs.target.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        let deltaY = (targetRect.bottom - TOOLTIP_OFFSET);
        let deltaX = (targetRect.left + (targetRect.width - tooltipRect.width) / 2);

        if (deltaX < TOOLTIP_MARGIN) {
            deltaX = TOOLTIP_MARGIN;
        } else if (deltaX + tooltipRect.width + TOOLTIP_MARGIN > window.innerWidth) {
            deltaX = (window.innerWidth - TOOLTIP_MARGIN - tooltipRect.width);
        }

        if (deltaY + tooltipRect.height + TOOLTIP_MARGIN > window.innerHeight) {
            deltaY = (targetRect.top - tooltipRect.height - TOOLTIP_OFFSET);
        }

        tooltip.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
    }

    renderTooltip() {
        const { content } = this.props;

        return (
            <div class="tooltip" onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave} onClick={stopPropagation}>
                {content}
            </div>
        );
    }

    render() {
        const { children, targetClass } = this.props;
        const { visible } = this.state;

        return (
            <div
                ref={noderef(this, "target")}
                class={targetClass}
                onClick={this.handleTargetClick}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}>
                {preact.toChildArray(children)}
                {visible && (
                    <Portal didRender={this.handleTooltipDidRender}>
                        {this.renderTooltip()}
                    </Portal>
                )}
            </div>
        );
    }
}

Tooltip.propTypes = {
    /** Target element, hovering over it triggers the tooltip */
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.node,
        PropTypes.element,
        PropTypes.object
    ]),
    /** The content that will be displayed inside of the tooltip. */
    content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.element
    ]).isRequired,
    /** Delay showing the tooltip (ms) */
    delay: PropTypes.number,
    /** A class name applied to the element that wraps the tooltip's target */
    targetClass: PropTypes.string
};

Tooltip.defaultProps = {
    delay: 500
};

export default Tooltip;
